import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig"; // Import Firebase config
import './display.css'; // Custom CSS for display and responsiveness
import { collection, getDocs } from "firebase/firestore";
import { FaCopy } from 'react-icons/fa'; // Import FontAwesome copy icon
import axios from './../../node_modules/axios/lib/axios';
import * as cheerio from "cheerio";

const DisplayTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'buy', or 'sell'
  const [selectedScrip, setSelectedScrip] = useState('all'); // 'all' or specific scrip
  const [isModalOpen, setIsModalOpen] = useState(false); // Control for modal visibility
  const [holdings, setHoldings] = useState({});
  const [ltpData, setLtpData] = useState({});
  const [manualLtp, setManualLtp] = useState({});
  const [selectedPartner, setSelectedPartner] = useState("Personal");

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "transactions"));
        const fetchedTransactions = [];

        querySnapshot.forEach((doc) => {
          fetchedTransactions.push({ ...doc.data(), id: doc.id });
        });

        setTransactions(fetchedTransactions);

        calculateHoldings(fetchedTransactions);


      } catch (error) {
        console.error("Error fetching transactions: ", error);
      }
    };

    fetchTransactions();
  }, []);

  useEffect(() => {
    calculateHoldings(transactions);
  }, [selectedPartner, transactions]);

  const calculateHoldings = (transactions) => {
    const holdingsMap = {};

    const filteredTx = transactions.filter(
      (tx) => selectedPartner === "all" || (tx.partner || "Personal") === selectedPartner
    );

    filteredTx.forEach((tx) => {
      const scrip = tx.scrip;

      if (!holdingsMap[scrip]) {
        holdingsMap[scrip] = {
          buyQty: 0,
          sellQty: 0,
        };
      }

      if (tx.transactionType === "BUY") {
        holdingsMap[scrip].buyQty += Number(tx.quantity);
      } else {
        holdingsMap[scrip].sellQty += Number(tx.quantity);
      }
    });

    const currentStocks = {};

    Object.keys(holdingsMap).forEach((scrip) => {
      const remaining = holdingsMap[scrip].buyQty - holdingsMap[scrip].sellQty;

      if (remaining > 0) {
        currentStocks[scrip] = remaining;
      }
    });

    setHoldings(currentStocks);
  };

  const calculateInvestment = (scrip) => {
    let totalBuyAmount = 0;
    let totalSellAmount = 0;

    const filteredTx = transactions.filter(
      (tx) =>
        tx.scrip === scrip &&
        (selectedPartner === "all" ||
          (tx.partner || "Personal") === selectedPartner)
    );

    filteredTx.forEach((tx) => {
      if (tx.transactionType === "BUY") {
        totalBuyAmount += Number(tx.amount);
      } else {
        totalSellAmount += Number(tx.amount);
      }
    });

    return totalBuyAmount - totalSellAmount;
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleScripChange = (e) => {
    setSelectedScrip(e.target.value);
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    alert("Transaction ID copied: " + id); // Display a small alert or notification
  };


  const filteredTransactions = transactions
    .filter(transaction =>
      (filter === "all" || transaction.transactionType === filter) &&
      (selectedScrip === "all" || transaction.scrip === selectedScrip) &&
      (selectedPartner === "all" || (transaction.partner || "Personal") === selectedPartner)
    )
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const uniqueScrips = [
    ...new Set(transactions.map((transaction) => transaction.scrip)),
  ];

  const uniquePartners = [
    ...new Set(transactions.map((tx) => tx.partner || "Personal")),
  ];

  const calculateStatistics = () => {

    const relevantTransactions = transactions.filter(t =>
      (selectedScrip === "all" || t.scrip === selectedScrip) &&
      (selectedPartner === "all" || (t.partner || "Personal") === selectedPartner)
    );
    const scripMap = {};

    let totalCommission = 0;

    relevantTransactions.forEach((tx) => {

      const shareAmount = tx.quantity * tx.rate;

      const commission =
        tx.transactionType === "BUY"
          ? tx.amount - shareAmount
          : shareAmount - tx.amount;

      totalCommission += commission;

      if (!scripMap[tx.scrip]) {
        scripMap[tx.scrip] = {
          buyQty: 0,
          sellQty: 0,
          buyAmount: 0,
          sellAmount: 0
        };
      }

      if (tx.transactionType === "BUY") {
        scripMap[tx.scrip].buyQty += Number(tx.quantity);
        scripMap[tx.scrip].buyAmount += Number(tx.amount);
      } else {
        scripMap[tx.scrip].sellQty += Number(tx.quantity);
        scripMap[tx.scrip].sellAmount += Number(tx.amount);
      }

    });

    let totalBillAmount = 0;
    let totalReceivableAmount = 0;

    Object.keys(scripMap).forEach((scrip) => {

      const stock = scripMap[scrip];

      // Skip scrips that are still in holdings
      if (holdings[scrip]) return;

      // Only completed trades
      if (stock.buyQty === stock.sellQty) {

        totalBillAmount += stock.buyAmount;
        totalReceivableAmount += stock.sellAmount;

      }

    });
    const grossNetProfitLoss = totalReceivableAmount - totalBillAmount;

    const totalCapitalGain =
      grossNetProfitLoss > 0
        ? grossNetProfitLoss * 0.075
        : 0;

    const netProfitLoss = grossNetProfitLoss - totalCapitalGain;

    return {

      totalTransactions: relevantTransactions.length,

      uniqueScripsCount: [
        ...new Set(relevantTransactions.map(t => t.scrip))
      ].length,

      totalScripQuantity: relevantTransactions.reduce(
        (sum, t) => sum + Number(t.quantity), 0
      ),

      totalBillAmount: totalBillAmount.toFixed(2),

      totalReceivableAmount: totalReceivableAmount.toFixed(2),

      totalCommission: totalCommission.toFixed(2),

      grossNetProfitLoss: grossNetProfitLoss.toFixed(2),

      totalCapitalGain: totalCapitalGain.toFixed(2),

      netProfitLoss: netProfitLoss.toFixed(2)

    };

  };
  const stats = calculateStatistics();
  const calculateUPL = (qty, ltp, investment) => {
    const sellValue = qty * ltp;
    const sellCommission = sellValue * 0.0036;
    const netSellValue = sellValue - sellCommission;

    const profit = netSellValue - investment;
    const capitalGainTax = profit > 0 ? profit * 0.075 : 0;

    return netSellValue - investment - capitalGainTax;
  };


  return (
    <div className="container">
      <div className="filter-container">
        <label>
          Filter Transactions by Type:
          <select value={filter} onChange={handleFilterChange}>
            <option value="all">All</option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
          </select>
        </label>
        <label>
          Filter by Scrip:
          <select value={selectedScrip} onChange={handleScripChange}>
            <option value="all">All</option>
            {uniqueScrips.map(scrip => (
              <option key={scrip} value={scrip}>{scrip}</option>
            ))}
          </select>
        </label>
        <label>
          Filter by Partner:
          <select value={selectedPartner} onChange={(e) => setSelectedPartner(e.target.value)}>
            <option value="all">All</option>
            {uniquePartners.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Display Statistics */}

      <div className="statistics-container">
        <div className="stat-card">
          <h2>Total Txns</h2>
          <p>{stats.totalTransactions}</p>
        </div>
        <div className="stat-card">
          <h2>Scrips</h2>
          <p>{stats.uniqueScripsCount}</p>
        </div>
        <div className="stat-card">
          <h2>Quantity</h2>
          <p>{stats.totalScripQuantity}</p>
        </div>
        <div className="stat-card">
          <h2>Payable Amount</h2>
          <p>{stats.totalBillAmount}</p>
        </div>
        <div className="stat-card">
          <h2>Receivable Amount</h2>
          <p>{stats.totalReceivableAmount}</p>
        </div>
        <div className="stat-card">
          <h2>Commission</h2>
          <p>{stats.totalCommission}</p>
        </div>
        <div className="stat-card">
          <h2>{stats.grossNetProfitLoss > 0 ? "Gross Profit" : "Gross Loss"}</h2>
          <p>{stats.grossNetProfitLoss}</p>
        </div>
        <div className="stat-card">
          <h2>Capital Gain (7.5%)</h2>
          <p>{stats.totalCapitalGain}</p>
        </div>


        <div className="stat-card">
          <h2>{stats.netProfitLoss > 0 ? "Net Profit" : "Net Loss"}</h2>
          <p>{stats.netProfitLoss}</p>
        </div>
        {Object.keys(holdings).map((scrip) => {

          const qty = holdings[scrip];

          const totalInvestment = calculateInvestment(scrip);

          return (
            <>
              <div className="stat-card" key={scrip}>
                <h2>{scrip}: {qty}</h2>

                <p>
                  LTP:
                  <input
                    type="number"
                    className="ltp-input"
                    value={manualLtp[scrip] || ""}
                    placeholder="0000"
                    max="9999"
                    onChange={(e) =>
                      setManualLtp({
                        ...manualLtp,
                        [scrip]: Number(e.target.value),
                      })
                    }
                  />
                </p>

                <p>
                  Value: {manualLtp[scrip] ? (qty * manualLtp[scrip]).toFixed(2) : "0.00"}
                </p>
              </div>

              <div className="stat-card">
                <h2>{scrip}: {qty}</h2>

                <p>
                  Investment: {totalInvestment.toFixed(2)}
                </p>

                <p>
                  UPL: {manualLtp[scrip]
                    ? calculateUPL(qty, manualLtp[scrip], totalInvestment).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </>
          );
        })}
      </div>

      {/* Display Data */}
      <table className="transaction-table">
        <thead>
          <tr>
            <th>Id</th>
            <th>Date</th>
            <th>Scrip</th>
            <th>Buy/Sell</th>
            <th>Quantity</th>
            <th>Rate</th>
            <th>B.Amt/R.Amt</th>
            <th>Share Amount</th>
            <th>Commission</th>
            <th>C.Rate</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map((transaction) => {
            const shareAmount = (transaction.quantity * transaction.rate).toFixed(2);
            const billOrReceivable = transaction.amount
            const commission = (transaction.transactionType === "BUY"
              ? transaction.amount - shareAmount
              : shareAmount - transaction.amount).toFixed(2);
            const rateWithCommission =
              (billOrReceivable / transaction.quantity).toFixed(2);

            return (
              <tr key={transaction.id}>
                <td>
                  <FaCopy
                    className="copy-icon"
                    onClick={() => handleCopyId(transaction.id)}
                  />
                </td>
                <td>{transaction.date}</td>
                <td>{transaction.scrip}</td>
                <td>{transaction.transactionType}</td>
                <td>{transaction.quantity}</td>
                <td>{transaction.rate}</td>
                <td>{billOrReceivable}</td>
                <td>{shareAmount}</td>
                <td>{commission}</td>
                <td>{rateWithCommission}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DisplayTransactions;
